import {
  IAccommodationBlockingForm,
  MAX_ACCOMMODATIONS,
  MAX_COUNTRIES,
  MAX_DESTINATIONS,
  MAX_DURATIONS_LENGTH,
  MAX_PUBLICATIONS,
  MAX_REGIONS,
  MAX_REMARK_LENGTH,
  MAX_TRANSPORT_HUBS,
} from './accommodation-blocking-form';
import { isValidDurations } from '../accommodation-blocking-duration/accommodation-blocking-duration';

const DURATIONS_FORMAT_MESSAGE =
  'Durations must be a comma-separated list of positive integers or ranges (e.g. 1,3-5), ' +
  'in strictly ascending order, without duplicates, overlaps, leading zeros or spaces.';

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

// Returns every currently broken Business Rule as a user-facing message, in form order.
// Rules that require server-side data (geography expansion, accommodation-to-geography
// matching) are intentionally excluded here - see ADR-0003.
// `today` is injectable so callers (and tests) can pin the reference date.
export function validateAccommodationBlocking(form: IAccommodationBlockingForm, today: Date = new Date()): string[] {
  const violations: string[] = [];
  const {
    publications,
    countries,
    regions,
    destinations,
    accommodations,
    airportsOrBusStops,
    dateRange,
    direction,
    blockingType,
    remark,
    durations,
  } = form;
  const { dateFrom, dateTo, openDate } = dateRange;

  if (publications.length > MAX_PUBLICATIONS) {
    violations.push(`Select no more than ${MAX_PUBLICATIONS} Publications.`);
  }

  if (countries.length > MAX_COUNTRIES) {
    violations.push(`Select no more than ${MAX_COUNTRIES} Countries.`);
  }

  if (regions.length > MAX_REGIONS) {
    violations.push(`Select no more than ${MAX_REGIONS} Regions.`);
  }

  if (destinations.length > MAX_DESTINATIONS) {
    violations.push(`Select no more than ${MAX_DESTINATIONS} Destinations.`);
  }

  if (accommodations.length > MAX_ACCOMMODATIONS) {
    violations.push(`Select no more than ${MAX_ACCOMMODATIONS} Accommodations.`);
  }

  const hasGeography = countries.length > 0 || regions.length > 0 || destinations.length > 0;
  const hasAccommodations = accommodations.length > 0;

  if (!hasGeography && !hasAccommodations) {
    violations.push('Select at least one Country, Region, Destination or Accommodation.');
  }

  if (airportsOrBusStops.length > MAX_TRANSPORT_HUBS) {
    violations.push(`Select no more than ${MAX_TRANSPORT_HUBS} Airports / Bus Stops.`);
  }

  const hasTransportHubs = airportsOrBusStops.length > 0;

  if (hasTransportHubs && !hasGeography && !hasAccommodations) {
    violations.push('Airports / Bus Stops require at least one Country, Region, Destination or Accommodation.');
  }

  if (hasTransportHubs && blockingType !== 'checkIn' && blockingType !== 'checkOut') {
    violations.push('Airports / Bus Stops can only be set when Type is Check In or Check Out.');
  }

  if (hasTransportHubs && !direction) {
    violations.push('Direction is required when Airports / Bus Stops are selected.');
  }

  if (!dateFrom) {
    violations.push('Date From is required.');
  }

  if (!openDate && !dateTo) {
    violations.push('Date To is required.');
  }

  if (dateFrom && startOfDay(dateFrom) < startOfDay(today)) {
    violations.push('Date From must be today or later.');
  }

  if (!openDate && dateFrom && dateTo && startOfDay(dateTo) < startOfDay(dateFrom)) {
    violations.push('Date To must be the same as Date From or later.');
  }

  if (!blockingType) {
    violations.push('Type is required.');
  }

  if (!remark.trim()) {
    violations.push('Remark is required.');
  } else if (remark.length > MAX_REMARK_LENGTH) {
    violations.push(`Remark must be ${MAX_REMARK_LENGTH} characters or fewer.`);
  }

  // Defensive: form.durations is typed as a non-null string (see accommodation-blocking-form.ts),
  // normalized from IAccommodationBlocking.durations (string | null) by mapAccommodationBlockingToForm.
  // The `?? ''` guards against a null slipping through if that normalization is ever bypassed.
  const trimmedDurations = (durations ?? '').trim();
  if (trimmedDurations) {
    if (trimmedDurations.length > MAX_DURATIONS_LENGTH) {
      violations.push(`Durations must be ${MAX_DURATIONS_LENGTH} characters or fewer.`);
    } else if (!isValidDurations(trimmedDurations)) {
      violations.push(DURATIONS_FORMAT_MESSAGE);
    }
  }

  return violations;
}
